
import { getPartnerDetailData, getRelicDetailData } from './buildRoutes';
import type { SynergySnapshot } from './SynergySystem';

export class TooltipOverlay {
  private root?: HTMLDivElement;

  mount() {
    if (this.root) return;
    this.root = document.createElement('div');
    this.root.className = 'tooltip-overlay-root';
    this.root.style.position = 'fixed';
    this.root.style.inset = '0';
    this.root.style.pointerEvents = 'none';
    this.root.style.zIndex = '99999';
    document.body.appendChild(this.root);

    window.addEventListener('show-item-tooltip', (e: Event) => {
      const ce = e as CustomEvent;
      this.showTooltip(ce.detail);
    });

    window.addEventListener('hide-item-tooltip', () => {
      this.hideTooltip();
    });
  }

  destroy() {
    this.root?.remove();
    this.root = undefined;
  }

  private hideTooltip() {
    if (this.root) {
      this.root.replaceChildren();
      this.root.style.pointerEvents = 'none';
    }
  }

  private showTooltip(detail: any) {
    if (!this.root) return;
    this.hideTooltip();

    this.root.style.pointerEvents = 'auto'; // allow clicking background to close

    const bg = document.createElement('div');
    bg.style.position = 'absolute';
    bg.style.inset = '0';
    bg.style.backgroundColor = 'rgba(0,0,0,0.6)';
    bg.addEventListener('pointerdown', () => this.hideTooltip());
    this.root.appendChild(bg);

    const panel = document.createElement('div');
    panel.style.position = 'absolute';
    panel.style.left = '50%';
    panel.style.top = '50%';
    panel.style.transform = 'translate(-50%, -50%)';
    panel.style.width = '320px';
    panel.style.backgroundColor = '#1a1a24';
    panel.style.border = '2px solid #b59b72';
    panel.style.borderRadius = '12px';
    panel.style.padding = '16px';
    panel.style.color = '#fff';
    panel.style.fontFamily = 'Arial, sans-serif';
    panel.style.boxShadow = '0 10px 30px rgba(0,0,0,0.8)';
    panel.addEventListener('pointerdown', (e) => e.stopPropagation()); // don't close if clicking panel

    if (detail.type === 'partner') {
      const data = getPartnerDetailData(detail.item, detail.level);
      panel.innerHTML = `
        <div style="display: flex; justify-content: space-between; align-items: flex-start;">
          <h2 style="margin: 0; color: #facc15; font-size: 20px;">${data.name} <span style="font-size:14px; color:#aaa">Lv.${data.level}</span></h2>
        </div>
        <div style="margin-top: 8px; display: flex; gap: 4px;">
          ${data.routes.map(r => `<span style="background:${r.cssColor}33; color:${r.cssColor}; border:1px solid ${r.cssColor}88; padding: 2px 6px; border-radius: 4px; font-size: 11px; font-weight: bold;">${r.label}</span>`).join('')}
        </div>
        <p style="margin: 12px 0; font-size: 14px; color: #cbd5e1; line-height: 1.4;">${data.attackDescription}</p>
        <div style="background: #2a2a35; padding: 8px; border-radius: 8px; margin-top: 12px;">
          <div style="font-size: 12px; color: #94a3b8; margin-bottom: 4px;">当前等级 (Lv.${data.current.level})</div>
          <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 4px; font-size: 13px;">
            <div>伤害 <span style="color:#4ade80">x${data.current.damageMultiplier.toFixed(2)}</span></div>
            <div>攻速 <span style="color:#60a5fa">x${data.current.attacksPerSecondMultiplier.toFixed(2)}</span></div>
            <div>射程 <span style="color:#f472b6">x${data.current.rangeMultiplier.toFixed(2)}</span></div>
          </div>
        </div>
        ${data.next ? `
        <div style="background: #3f3f2a; border: 1px solid #ca8a04; padding: 8px; border-radius: 8px; margin-top: 8px;">
          <div style="font-size: 12px; color: #fde047; margin-bottom: 4px;">⬆ 升星预览 (Lv.${data.next.level})</div>
          <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 4px; font-size: 13px;">
            <div>伤害 <span style="color:#4ade80">x${data.next.damageMultiplier.toFixed(2)}</span></div>
            <div>攻速 <span style="color:#60a5fa">x${data.next.attacksPerSecondMultiplier.toFixed(2)}</span></div>
            <div>射程 <span style="color:#f472b6">x${data.next.rangeMultiplier.toFixed(2)}</span></div>
          </div>
        </div>
        ` : '<div style="margin-top: 8px; text-align: center; color: #facc15; font-size: 12px; font-weight: bold;">已达到最高星级</div>'}
      `;
    } else if (detail.type === 'relic') {
      const data = getRelicDetailData(detail.item, detail.stacks);
      panel.innerHTML = `
        <div style="display: flex; justify-content: space-between; align-items: flex-start;">
          <h2 style="margin: 0; color: #facc15; font-size: 20px;">${data.name}</h2>
          <span style="font-size:12px; font-weight:bold; background:${data.rarityColor === 0xffffff ? '#444' : '#' + data.rarityColor.toString(16)}; padding:2px 6px; border-radius:4px; color:#000;">${data.rarityLabel}</span>
        </div>
        <div style="margin-top: 8px; display: flex; gap: 4px;">
          ${data.routes.map(r => `<span style="background:${r.cssColor}33; color:${r.cssColor}; border:1px solid ${r.cssColor}88; padding: 2px 6px; border-radius: 4px; font-size: 11px; font-weight: bold;">${r.label}</span>`).join('')}
        </div>
        <p style="margin: 12px 0; font-size: 14px; color: #cbd5e1; line-height: 1.4;">${data.description}</p>
        <div style="background: #2a2a35; padding: 8px; border-radius: 8px; margin-top: 12px;">
          <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
            <span style="font-size: 12px; color: #94a3b8;">叠加规则</span>
            <span style="font-size: 12px; font-weight: bold; color: ${data.diminishingFactor ? '#f472b6' : '#4ade80'};">${data.stackingText}</span>
          </div>
          <div style="font-size: 14px; margin-top: 8px;">
            当前层数: <span style="font-size: 18px; font-weight: bold; color: #facc15;">${data.stacks}</span> 层
          </div>
        </div>
      `;
    }

    if (detail.synergies && detail.synergies.length > 0) {
      const synTitle = document.createElement('div');
      synTitle.style.marginTop = '16px';
      synTitle.style.fontSize = '12px';
      synTitle.style.fontWeight = 'bold';
      synTitle.style.color = '#94a3b8';
      synTitle.style.borderBottom = '1px solid #475569';
      synTitle.style.paddingBottom = '4px';
      synTitle.style.marginBottom = '8px';
      synTitle.textContent = '当前阵型羁绊进度';
      panel.appendChild(synTitle);

      detail.synergies.forEach((syn: SynergySnapshot) => {
        const row = document.createElement('div');
        row.style.display = 'flex';
        row.style.alignItems = 'center';
        row.style.justifyContent = 'space-between';
        row.style.marginBottom = '4px';
        row.style.fontSize = '13px';
        
        let activeText = '';
        if (syn.tiers.length > 0) {
          activeText = `<span style="color:#facc15; font-weight:bold;">Lv.${syn.tiers[syn.tiers.length-1]} 激活</span>`;
        } else {
          // need 2 for first tier
          activeText = `<span style="color:#64748b">差 ${2 - syn.count} 激活</span>`;
        }

        row.innerHTML = `
          <div style="display:flex; align-items:center; gap:6px;">
            <div style="width:24px; height:24px; background:#334155; border-radius:12px; display:flex; justify-content:center; align-items:center; font-weight:bold; color:#f8fafc; border: 2px solid ${syn.tiers.length > 0 ? '#facc15' : '#475569'}">${syn.count}</div>
            <span style="font-weight:bold; color:${syn.tiers.length > 0 ? '#f8fafc' : '#94a3b8'}">${syn.tag}</span>
          </div>
          <div>${activeText}</div>
        `;
        panel.appendChild(row);
      });
    }

    const closeBtn = document.createElement('button');
    closeBtn.textContent = '关闭详情';
    closeBtn.style.width = '100%';
    closeBtn.style.marginTop = '16px';
    closeBtn.style.padding = '10px';
    closeBtn.style.backgroundColor = '#475569';
    closeBtn.style.color = '#fff';
    closeBtn.style.border = 'none';
    closeBtn.style.borderRadius = '8px';
    closeBtn.style.fontWeight = 'bold';
    closeBtn.addEventListener('click', () => this.hideTooltip());
    panel.appendChild(closeBtn);

    this.root.appendChild(panel);
  }
}
